// Self-hosted auth client for the Worker's /api/auth endpoints. It keeps the
// same call shapes the app used before (signInWithOtp, verifyOtp, getSession,
// onAuthStateChange, signOut), persists the session in localStorage, and
// refreshes the access token before it expires. detectSessionInUrl: false —
// codes are typed in, never carried in links.

type AppUser = { id: string; email?: string; user_metadata?: Record<string, unknown> };
type AppSession = { access_token: string; refresh_token: string; expires_at: number; user: AppUser };
type AuthResult<T> = { data: T; error: { message: string } | null };

const STORAGE_KEY = "koast-auth";
const listeners = new Set<(session: AppSession | null) => void>();
let refreshing: Promise<AppSession | null> | null = null;

function readStored(): AppSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as AppSession;
    return session?.access_token && session?.refresh_token ? session : null;
  } catch {
    return null;
  }
}

function store(session: AppSession | null) {
  try {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* private-mode storage failures never block auth */ }
  for (const listener of listeners) listener(session);
}

async function post<T>(path: string, body: Record<string, unknown>): Promise<{ ok: boolean; status: number; payload: T & { message?: string } }> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({})) as T & { message?: string };
  return { ok: response.ok, status: response.status, payload };
}

async function refresh(current: AppSession): Promise<AppSession | null> {
  refreshing ??= (async () => {
    const { ok, payload } = await post<AppSession>("/api/auth/refresh", { refresh_token: current.refresh_token });
    const next = ok && payload.access_token ? payload : null;
    store(next);
    return next;
  })().finally(() => { refreshing = null; });
  return refreshing;
}

async function currentSession(): Promise<AppSession | null> {
  const session = readStored();
  if (!session) return null;
  // Refresh a couple of minutes early so in-flight requests never race expiry.
  if (session.expires_at * 1000 - Date.now() < 120_000) return refresh(session);
  return session;
}

export const supabase = {
  auth: {
    async getSession(): Promise<{ data: { session: AppSession | null } }> {
      return { data: { session: await currentSession() } };
    },

    onAuthStateChange(callback: (event: string, session: AppSession | null) => void) {
      const listener = (session: AppSession | null) => callback(session ? "SIGNED_IN" : "SIGNED_OUT", session);
      listeners.add(listener);
      return { data: { subscription: { unsubscribe: () => { listeners.delete(listener); } } } };
    },

    async signInWithOtp(options: { email: string; options?: { shouldCreateUser?: boolean; data?: Record<string, unknown> } }): Promise<{ error: { message: string } | null }> {
      const { ok, payload } = await post("/api/auth/otp", {
        email: options.email,
        create: options.options?.shouldCreateUser === true,
        business_name: String(options.options?.data?.business_name || ""),
      });
      return { error: ok ? null : { message: payload.message || "The code could not be sent." } };
    },

    // `type` is accepted for call-site compatibility; the Worker validates the
    // newest open code for the email regardless of how it was issued.
    async verifyOtp(options: { email: string; token: string; type?: string }): Promise<AuthResult<{ session: AppSession | null }>> {
      const { ok, payload } = await post<AppSession>("/api/auth/verify", { email: options.email, code: options.token });
      if (!ok || !payload.access_token) {
        return { data: { session: null }, error: { message: payload.message || "That code could not be verified." } };
      }
      store(payload);
      return { data: { session: payload }, error: null };
    },

    async signOut(): Promise<{ error: null }> {
      const session = readStored();
      if (session) await post("/api/auth/signout", { refresh_token: session.refresh_token }).catch(() => undefined);
      store(null);
      return { error: null };
    },
  },
};
